import { useState } from "react";
import { Loader2, Send, CheckCircle2, XCircle } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const KindSelect = ({ value, onChange, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="input text-xs py-1.5 w-40">
        <option value="payin">PayIn callbacks</option>
        <option value="payout">Payout webhooks</option>
    </select>
);

export const SendTest = () => {
    const [kind, setKind] = useState("payin");
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    const [err, setErr] = useState("");
    const send = async () => {
        setBusy(true); setErr(""); setRes(null);
        try { setRes((await api.post(`/partner/webhook-signing/${kind}/send-test`)).data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    return (
        <div className="border border-slate-200 rounded-lg p-4 bg-white space-y-3" data-testid="wh-sign-send-test">
            <div className="font-semibold text-sm">1. Send a signed test webhook to your endpoint</div>
            <div className="flex items-center gap-2">
                <KindSelect value={kind} onChange={setKind} testid="wh-sign-test-kind" />
                <button type="button" onClick={send} disabled={busy} data-testid="wh-sign-test-send"
                        className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test
                </button>
            </div>
            {err && <div className="text-xs text-rose-600" data-testid="wh-sign-test-error">{err}</div>}
            {res && (
                <div className="text-xs space-y-1.5" data-testid="wh-sign-test-result">
                    <div className={`flex items-center gap-1.5 font-semibold ${res.ok ? "text-emerald-700" : "text-rose-700"}`}>
                        {res.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {res.status_code ? `Your server replied HTTP ${res.status_code}` : res.error}
                    </div>
                    <div className="font-mono text-[11px] break-all"><span className="text-slate-500">X-PayGateway-Signature: </span>{res.signature}</div>
                    <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] overflow-x-auto whitespace-pre-wrap break-all">{res.body}</pre>
                    {res.response_excerpt && <div className="text-slate-500">Response: <span className="font-mono">{res.response_excerpt}</span></div>}
                </div>
            )}
        </div>
    );
};

export const SignatureChecker = () => {
    const [kind, setKind] = useState("payin");
    const [body, setBody] = useState("");
    const [sig, setSig] = useState("");
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    const [err, setErr] = useState("");
    const check = async () => {
        setBusy(true); setErr(""); setRes(null);
        try { setRes((await api.post("/partner/webhook-signing/verify", { kind, body, signature: sig })).data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    return (
        <div className="border border-slate-200 rounded-lg p-4 bg-white space-y-3" data-testid="wh-sign-checker">
            <div className="font-semibold text-sm">2. Check the signature your code computed</div>
            <p className="text-xs text-slate-500">Paste the exact raw body you received and the signature your server calculated. We'll tell you whether it matches.</p>
            <KindSelect value={kind} onChange={setKind} testid="wh-sign-check-kind" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} data-testid="wh-sign-check-body"
                      placeholder='{"event":"payin.captured", ...}' className="input w-full font-mono text-[11px]" />
            <input value={sig} onChange={(e) => setSig(e.target.value)} data-testid="wh-sign-check-signature"
                   placeholder="hex signature your code produced" className="input w-full font-mono text-[11px]" />
            <button type="button" onClick={check} disabled={busy || body.length < 2 || !sig} data-testid="wh-sign-check-submit"
                    className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Verify signature
            </button>
            {err && <div className="text-xs text-rose-600" data-testid="wh-sign-check-error">{err}</div>}
            {res && (
                <div data-testid="wh-sign-check-result" className={`text-xs rounded-md p-2.5 border ${res.match ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                    <div className="font-semibold flex items-center gap-1.5">
                        {res.match ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {res.match ? "Signature matches — your verification code is correct." : "Signature does NOT match."}
                    </div>
                    {!res.match && (
                        <div className="mt-1.5 space-y-1">
                            <div className="font-mono text-[11px] break-all">Expected: {res.expected_signature}</div>
                            <div>Common causes: body was re-serialised/pretty-printed, wrong secret (PayIn vs Payout), or trailing newline ({res.body_bytes} bytes checked).</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
